import {
  type Currency,
  formatPlanPrice,
  PLAN_CATALOG,
  type PlanLadder,
  type PlanMeta,
  plansForLadder,
} from '@devizy/shared';
import { Badge, Button, toast } from '@devizy/ui';
import { ArrowSquareOut, CheckCircle, CreditCard, XCircle } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { OverdueCommissionCard } from '@/components/billing';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/settings/plan')({
  component: PlanPage,
});

const CARD =
  'border-border bg-card rounded-2xl border p-6 shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)]';

const LADDER_LABEL: Record<PlanLadder, string> = {
  REQUESTER: 'Plans Requester',
  BIDDER: 'Plans Bidder',
};

const PAID_PLAN_IDS = ['PLUS', 'PREMIUM', 'PRO', 'BUSINESS'] as const;
type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

function isPaidPlanId(id: string): id is PaidPlanId {
  return (PAID_PLAN_IDS as readonly string[]).includes(id);
}

function planLabel(id: string): string {
  return PLAN_CATALOG.find((plan) => plan.id === id)?.label ?? id;
}

function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface MeFlags {
  isAdmin: boolean;
  hasRequesterProfile: boolean;
  hasBidderProfile: boolean;
  role: 'REQUESTER' | 'BIDDER' | 'ADMIN';
}

function resolveLadders(me: MeFlags | null | undefined): PlanLadder[] {
  if (!me) return ['REQUESTER'];
  if (me.isAdmin) return ['REQUESTER', 'BIDDER'];
  const ladders: PlanLadder[] = [];
  if (me.hasRequesterProfile) ladders.push('REQUESTER');
  if (me.hasBidderProfile) ladders.push('BIDDER');
  if (ladders.length === 0) ladders.push(me.role === 'BIDDER' ? 'BIDDER' : 'REQUESTER');
  return ladders;
}

function PlanCard({
  plan,
  currency,
  isCurrent,
  isPending,
  onSubscribe,
}: {
  plan: PlanMeta;
  currency: Currency;
  isCurrent: boolean;
  isPending: boolean;
  onSubscribe: (id: PaidPlanId) => void;
}): React.ReactElement {
  return (
    <div
      className={`flex flex-col gap-4 rounded-2xl border p-5 transition-shadow ${
        isCurrent
          ? 'border-primary/40 bg-primary-soft'
          : 'border-border bg-card shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{plan.label}</span>
        {isCurrent ? (
          <Badge variant="default" className="text-xs">
            Actif
          </Badge>
        ) : null}
      </div>
      <p className="flex items-baseline gap-1">
        <span className="font-mono text-2xl font-bold tabular-nums">
          {formatPlanPrice(plan, currency)}
        </span>
        {plan.paid ? <span className="text-muted-foreground text-sm">/ mois</span> : null}
      </p>
      <ul className="flex-1 space-y-1.5">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <CheckCircle weight="fill" className="text-primary mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        <Button variant="outline" size="sm" disabled>
          Plan actuel
        </Button>
      ) : plan.paid && isPaidPlanId(plan.id) ? (
        <Button
          variant="primary"
          size="sm"
          disabled={isPending}
          onClick={() => {
            if (isPaidPlanId(plan.id)) onSubscribe(plan.id);
          }}
        >
          {isPending ? 'Redirection…' : `Passer à ${plan.label}`}
        </Button>
      ) : (
        <Button variant="ghost" size="sm" disabled>
          Offre par défaut
        </Button>
      )}
    </div>
  );
}

interface PortalData {
  generalUrl: string;
  subscriptions: {
    paddleSubId: string;
    kind: 'PLAN' | 'BIDDER_CARD';
    plan: string | null;
    cancelUrl: string;
    updatePaymentMethodUrl: string;
  }[];
}

function portalRowLabel(sub: PortalData['subscriptions'][number]): string {
  return sub.kind === 'BIDDER_CARD' ? 'Moyen de paiement Bidder' : planLabel(sub.plan ?? '');
}

function ManageSection({ portal }: { portal: PortalData }): React.ReactElement {
  return (
    <section className={CARD}>
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-base">Gérer l&apos;abonnement</h3>
        <p className="text-muted-foreground text-xs">
          Factures et moyen de paiement sont gérés via un portail de paiement sécurisé.
        </p>
      </div>
      <div className="mt-4">
        <Button size="sm" onClick={() => openExternal(portal.generalUrl)}>
          <CreditCard weight="bold" className="mr-1.5 h-4 w-4" />
          Ouvrir le portail
        </Button>
      </div>
      {portal.subscriptions.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {portal.subscriptions.map((sub) => (
            <li
              key={sub.paddleSubId}
              className="border-border bg-background flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4"
            >
              <span className="text-sm font-medium">{portalRowLabel(sub)}</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openExternal(sub.updatePaymentMethodUrl)}
                >
                  <ArrowSquareOut weight="bold" className="mr-1.5 h-4 w-4" />
                  Mettre à jour le paiement
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => openExternal(sub.cancelUrl)}
                >
                  <XCircle weight="bold" className="mr-1.5 h-4 w-4" />
                  Annuler
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PlanPage(): React.ReactElement {
  const me = trpc.user.me.useQuery();
  const planQuery = trpc.billing.currentPlan.useQuery();
  const currentPlan = planQuery.data?.plan ?? 'FREE';
  const currency: Currency = me.data?.currency ?? 'EUR';

  const portal = trpc.billing.customerPortal.useQuery(undefined, {
    enabled: currentPlan !== 'FREE' || (me.data?.hasBidderProfile ?? false),
    retry: false,
  });

  const subscribe = trpc.billing.subscribe.useMutation({
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
    onError: (err) => toast.error(err.message),
  });

  const ladders = resolveLadders(me.data);
  const isLoading = me.isLoading || planQuery.isLoading;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-display text-base tracking-tight">Plan &amp; facturation</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Choisissez votre offre. La devise ({currency}) est figée à la création du compte —
          modifiable uniquement via le support.
        </p>
      </div>

      {me.data?.hasBidderProfile ? <OverdueCommissionCard /> : null}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-muted/40 h-56 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          {ladders.map((ladder) => (
            <section key={ladder} className="flex flex-col gap-4">
              <h3 className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                {LADDER_LABEL[ladder]}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {plansForLadder(ladder).map((plan) => (
                  <PlanCard
                    key={`${ladder}-${plan.id}`}
                    plan={plan}
                    currency={currency}
                    isCurrent={currentPlan === plan.id}
                    isPending={subscribe.isPending}
                    onSubscribe={(id) => subscribe.mutate({ plan: id })}
                  />
                ))}
              </div>
            </section>
          ))}

          {portal.data ? <ManageSection portal={portal.data} /> : null}
        </>
      )}
    </div>
  );
}
